/**
 * Copyright 2018 OpenStack Foundation
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 * http://www.apache.org/licenses/LICENSE-2.0
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 **/

import {adminGroups, superAdmin} from "../model/groups";
import {getAccessToken} from 'openstack-uicore-foundation/lib/security/methods'
import { initLogOut} from 'openstack-uicore-foundation/lib/security/methods';

 export const nowBetween = (now, start, end) => {
     now = fromMillisToSec(now);
     return now > start  && now < end;
 }

 export const moveItem = (array, fromIndex, toIndex) => {
     if (fromIndex === toIndex) return array;

     const newArray = [...array];

     const target = newArray[fromIndex];
     const inc = toIndex < fromIndex ? -1 : 1;

     for (let i = fromIndex; i !== toIndex; i += inc) {
         newArray[i] = newArray[i + inc];
     }

     newArray[toIndex] = target;

     return newArray;
 };

 export const getInitials = string => {
     return string.split(" ").map(n => n[0]).join("");
 };

 export const isSuperAdmin = (member) => {
     return member.groups.some(g => superAdmin.includes(g.code));
 };

export const isAdmin = (member) => {
    return !!member?.groups?.some(g => adminGroups.includes(g.code));
};

export const getAccessTokenSafely = async () => {
  try {
    return await getAccessToken();
  }
  catch (e) {
    console.log('log out: ', e);
    initLogOut();
  }
};

/**
 * @param person
 * @returns {`${*} ${*} ${string}`}
 */
export const formatPersonFullName = (person) => {
    return `${person.first_name} ${person.last_name}`;
}

/**
 * @param person
 * @param showCompany
 * @returns {`${*} ${*} ${string} <${string}>`}
 */
export const formatPersonEmailRFC = (person, showCompany = false) => {
    return `${formatPersonFullName(person, showCompany)} <${person.email}>`;
}

export const getAvailableTracksFromSelectionplan = (sp) => {
    // get all tracks from the selection plan and track visible
    return sp?.track_groups.reduce((result, trackGroup) => {
        trackGroup?.tracks?.forEach(track => {
            if (!result.find(tr => tr.id === track.id)) {
                result.push(track);
            }
        });
        return result;
    }, []).filter(t => t?.chair_visible);
}

export const getTrackForuser = (sp, member, loggedTrackChair) => {
    // selection plan does no has any track group
    if(!sp.track_groups) return [];
    const spTracks = getAvailableTracksFromSelectionplan(sp);
    if(!spTracks.length) return [];

    const memberIsAdmin = member.groups.some(g => adminGroups.includes(g.code));
    return memberIsAdmin ? spTracks : spTracks.filter(t => !!loggedTrackChair?.categories?.includes(t.id));
}

export const canAccessToSelectionPlan = (sp, member, loggedTrackChair) => {
    // selection plan does no has any track group
    return getTrackForuser(sp, member, loggedTrackChair).length;
}

export function isEpochMillis(epoch) {
  return typeof epoch === 'number' && epoch > 10000000000;
}

export const fromMillisToSec = (epoch) => {
  if (!epoch) return 0;
  if(isEpochMillis(epoch)) return Math.floor(epoch / 1000);
  return Math.floor(epoch);
}
